import { type APIReferenceType, ArrowType, LineType } from '@ardoq/api-types';
import {
  type Arrow,
  type EdgeStyle,
  type Stroke,
  defaultArrow,
  defaultDashedStroke,
  defaultEdgeStyle,
  defaultSolidStroke,
} from '@ardoq/graph-view';
import { resolveColor } from 'tabview/assistantShared/legend';

/**
 * Ardoq encodes arrowheads with a rich enum (diamonds, circles, cardinality…);
 * the graph-view edge only distinguishes "has a decoration" from "none", so we
 * collapse to the default arrow shape or none. Exact arrowhead shapes are a
 * future refinement.
 */
const toArrowType = (arrow: ArrowType | undefined): Arrow['type'] =>
  !arrow || arrow === ArrowType.NONE ? 'none' : defaultArrow.type;

const toStroke = (color: string, line: LineType): Stroke => {
  switch (line) {
    case LineType.SOLID:
      return { ...defaultSolidStroke, color };
    case LineType.DOTTED:
      return { ...defaultDashedStroke, color, dashStyle: { pattern: [1, 2] } };
    case LineType.DASHED:
      return { ...defaultDashedStroke, color };
  }
};

/**
 * Builds a graph-view edge style from an Ardoq reference type — its color, line
 * pattern (solid/dashed/dotted) and source/target arrowheads. Falls back to the
 * neutral default style when the reference type is unknown.
 */
export const toEdgeStyle = (
  referenceType: APIReferenceType | null
): EdgeStyle => {
  if (!referenceType) return defaultEdgeStyle;
  const color = resolveColor(referenceType);
  return {
    ...defaultEdgeStyle,
    stroke: toStroke(color, referenceType.line),
    sourceArrow: {
      ...defaultArrow,
      type: toArrowType(referenceType.lineBeginning),
      color,
    },
    targetArrow: {
      ...defaultArrow,
      type: toArrowType(referenceType.lineEnding),
      color,
    },
  };
};
